import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import translate from '../../../commonComponents/translate';
import FormattedAddress from '../FormattedAddress';
import {
  findCurrencySymbol,
  formatPrice,
  newFormatPrice,
  PAYMENT_METHOD,
  SUBSCRIPTION_STATUS,
} from '../../../services/utilityFunctions';
import OrderHeaderItem from './OrderHeaderItem';
import SubscriptionProp from '../propTypes/subscriptionProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import UserProp from '../../../commonComponents/propTypes/userProp';
import StripeCardProp from '../../profile/propTypes/StripeCardProp';
import Notifications from '../../../services/notifications';
import * as Utils from '../../../services/utilityFunctions';
import UpdatePaymentMethod from '../modal/UpdatePaymentMethod';
import UpdateAddressModal from '../modal/UpdateAddressModal';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import ReactLoader from '../../../commonComponents/loader/ReactLoader';
import { useModal } from '../../../services/customHooks';
import BasicModal from '../../../commonComponents/modal/BasicModal';

function SubscriptionOrderHeader({
  t,
  subscription,
  countries,
  user,
  paramCards,
  currencies,
  _csrf,
  tenant,
}) {
  const {
    status,
    subscriptionStartDate,
    nextPaymentDate,
    companyBilling,
    billingAddress,
    shippingAddress,
    paymentMethodType,
    owner,
    discount,
    productParent,
    total,
    currency,
  } = subscription;
  const [modalUpdateAddress, setModalUpdateAddress] = useState({
    show: false,
    isShipping: false,
  });
  const [modalUpdatePayment, setModalUpdatePayment] = useState({ show: false });
  const showModalUpdateAddress = isShipping =>
    setModalUpdateAddress({ show: true, isShipping });
  const showModalUpdatePayment = () => setModalUpdatePayment({ show: true });

  const [isLoading, setIsLoading] = useState(false);
  const [showCancelSubscription, setShowCancelSubscription] = useModal({
    show: false,
    data: null,
  });
  const browserHistory = useHistory();
  const isPaypalPaymentMethod = paymentMethodType === PAYMENT_METHOD.PAYPAL;
  // Format address
  const renderSubscriptionOrderAddress = (
    address,
    isShippingAddress = false,
  ) => {
    let addressParam = address;
    const existInsideOfAddress =
      addressParam.firstName || addressParam.lastName;
    const existOwner = owner?.firstname || owner?.lastName || owner?.username;
    if (!existOwner && !existInsideOfAddress) {
      return `(${t('reports.marketplace.orders.user.removed')})`;
    }
    if (
      !isShippingAddress &&
      paymentMethodType === PAYMENT_METHOD.PAID_BY_COMPANY
    ) {
      addressParam = {
        ...addressParam,
        firstName: companyBilling?.name || '',
        lastName: '',
        email: companyBilling?.email || '',
        phone: companyBilling?.phone || '',
        emails: companyBilling?.emails || [],
      };
    }
    return (
      <FormattedAddress
        address={addressParam}
        showOptionalFields
        formatCompanyName={false}
      />
    );
  };

  const renderDiscount = discountObject => {
    const renderCouponValue = () => {
      let couponValue;
      if (discountObject?.amountOffAvailable) {
        const currencyToUse = discountObject.userPrice
          ? discountObject.userPrice.code
          : discountObject.currency;
        couponValue = `${newFormatPrice(
          discountObject.amountOffAvailable,
          true,
          currencyToUse,
        )}`;
      } else {
        couponValue = `${discountObject.percentOff}%`;
      }
      return <span className="coupon-discount py-0 my-0">{couponValue}</span>;
    };
    if (discountObject) {
      return (
        <div className="row mx-1 discount-coupon-card mb-2 px-2 vertical-center">
          <div className="col-3 text-center coupon-preview-container from-subscription p-2">
            <img
              src="/images/shop/coupon.svg"
              alt="Coupon preview"
              className="coupon-preview-img"
            />
          </div>
          <div className="col-lg-10 col-8 from-subscription container-coupon-name">
            <div className="wrapper-coupon-name">
              <div className="coupon-name">{discountObject.name}</div>
              {renderCouponValue()}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="discount-holder">
        <span>{t('order.subscription.noDiscounts')}</span>
      </div>
    );
  };

  const currentCard = paramCards
    .slice()
    ?.find(i => i.id === subscription.paymentMethodId);
  const cardNumberLast4 = currentCard
    ? Utils.displayCardNumber(currentCard.last4)
    : '';

  const cancelSubscription = useCallback(async () => {
    setIsLoading(true);
    axios({
      url: `/api/market/order/subscription/${subscription._id}/cancel`,
      method: 'PUT',
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('order.subscription.cancel.success'),
        );
        browserHistory.replace(`/shop/order/subscription/${subscription._id}`);
        browserHistory.go(0);
      })
      .catch(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(
          err,
          t('order.subscription.cancel.error'),
        );
        console.log(err);
      });
  }, [subscription._id, setIsLoading]);

  const handleCancelSubscription = useCallback(
    async (showPopup = false, isSave = false) => {
      if (isSave) {
        await cancelSubscription();
      }
      setShowCancelSubscription({ show: showPopup, data: null });
    },
    [cancelSubscription],
  );

  const image = productParent?.images[0];
  const imageURL =
    image && image?.thumb ? image?.thumb : '/images/no_image_upload.png';
  const statusImg =
    status === SUBSCRIPTION_STATUS.ACTIVE
      ? '/images/plan/active-icon.svg'
      : '/images/plan/canceled-icon.svg';
  return (
    <>
      {modalUpdateAddress.show && (
        <UpdateAddressModal
          subscription={subscription}
          t={t}
          isShipping={modalUpdateAddress.isShipping}
          onClose={() => {
            setModalUpdateAddress({ show: false, isShipping: false });
          }}
          user={user}
          countries={countries}
        />
      )}
      {modalUpdatePayment.show && (
        <UpdatePaymentMethod
          t={t}
          tenant={tenant}
          onClose={() => setModalUpdatePayment({ show: false })}
          _csrf={_csrf}
          subscription={subscription}
        />
      )}
      {showCancelSubscription.show ? (
        <BasicModal
          onClose={save => handleCancelSubscription(false, save)}
          id="showCancelSubscription"
          title={t('order.subscription.cancel')}
          saveMessage={t('yes')}
          cancelMessage={t('no')}
          showCloseOnTop
          isLoading={isLoading}
        >
          <span>{t('order.subscription.cancel.question')}</span>
        </BasicModal>
      ) : null}
      <ReactLoader isLoading={isLoading}>
        <div className="order-subscription-header-wrapper">
          <div className="product-thumbnail">
            <div className="product-thumbnail-item">
              <img src={imageURL} alt={productParent?.name} />
            </div>
          </div>
          <div className="item flex-end initial-info">
            <OrderHeaderItem
              containerClass="product-parent-name-wrapper"
              label={productParent?.name}
              value={`${formatPrice(
                total,
                findCurrencySymbol(currency, currencies) || '€',
              )}/ ${t(`order.subscription.${subscription.frequency}`)}`}
            />
            {status && (
              <div className="order-item-wrapper">
                <span className="label-item">
                  {t('order.subscription.status')}
                </span>
                <span className="value-item">
                  <img
                    src={statusImg}
                    alt="active"
                    className="active-checkbox"
                  />
                  {t(`shop.${status}`)}
                </span>
              </div>
            )}
            <div className="order-item-wrapper">
              {shippingAddress && (
                <>
                  <span className="label-item">
                    {t('order.subscription.shippingAddress')}
                    {(status === SUBSCRIPTION_STATUS.ACTIVE ||
                      status === SUBSCRIPTION_STATUS.FAILED) && (
                      <button
                        type="button"
                        onClick={() => showModalUpdateAddress(true)}
                        className="item-edit"
                      >
                        <i className="fa fa-pen" />
                      </button>
                    )}
                  </span>
                  <span className="value-item">
                    {renderSubscriptionOrderAddress(shippingAddress, true)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="item flex-end">
            {subscriptionStartDate && (
              // eslint-disable-next-line max-len
              <OrderHeaderItem
                label={t('order.subscription.startSubscription')}
                value={moment(subscriptionStartDate).format('DD MMMM YYYY')}
              />
            )}
            <div className="order-item-wrapper">
              {billingAddress && (
                <>
                  <span className="label-item">
                    {t('order.subscription.billingAddress')}
                    {(status === SUBSCRIPTION_STATUS.ACTIVE ||
                      status === SUBSCRIPTION_STATUS.FAILED) && (
                      <button
                        type="button"
                        onClick={() => showModalUpdateAddress(false)}
                        className="item-edit"
                      >
                        <i className="fa fa-pen" />
                      </button>
                    )}
                  </span>
                  <span className="value-item">
                    {renderSubscriptionOrderAddress(billingAddress)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="item align-center">
            {nextPaymentDate && (
              // eslint-disable-next-line max-len
              <OrderHeaderItem
                label={t('order.subscription.nextDelivery')}
                value={moment.utc(nextPaymentDate).format('DD MMMM YYYY')}
              />
            )}
            <OrderHeaderItem
              label={t('order.subscription.discounts')}
              value={renderDiscount(discount)}
            />
          </div>
          <div className="item align-start">
            <div className="subscription-actions">
              {(status === SUBSCRIPTION_STATUS.ACTIVE ||
                status === SUBSCRIPTION_STATUS.FAILED) && (
                <div className="cancel-button">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => handleCancelSubscription(true)}
                  >
                    {t('order.subscription.cancel')}
                  </button>
                </div>
              )}
            </div>
            {(cardNumberLast4?.length > 0 || isPaypalPaymentMethod) && (
              <div className="order-item-wrapper">
                <span className="label-item">
                  {t('order_detail_right.paymentMethod')}
                  {(status === SUBSCRIPTION_STATUS.ACTIVE ||
                    status === SUBSCRIPTION_STATUS.FAILED) && (
                    <button
                      type="button"
                      onClick={showModalUpdatePayment}
                      className="item-edit"
                    >
                      <i className="fa fa-pen" />
                    </button>
                  )}
                </span>
                <span className="value-item">
                  <span className="m-card_item__head">
                    <img
                      src={
                        !isPaypalPaymentMethod
                          ? '/images/shop/card.svg'
                          : '/images/shop/paypal.svg'
                      }
                      alt="Credit Card preview"
                      className="card-preview-img"
                    />
                    <span className="m-card_item__title">
                      {!isPaypalPaymentMethod ? cardNumberLast4 : ''}
                    </span>
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </ReactLoader>
    </>
  );
}

SubscriptionOrderHeader.propTypes = {
  t: PropTypes.func.isRequired,
  subscription: PropTypes.shape(SubscriptionProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  paramCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape({}).isRequired,
};

export default translate(SubscriptionOrderHeader);
